import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
  CircularProgress,
  Collapse,
  FormGroup,
  Grid,
  FormControl,
  MenuItem,
  FormLabel,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { TopNavBar } from 'components/top_nav_bar';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { CuffaMeasurementUnitEnum, CuffaProductionTypeEnum, CuffaProductTypeEnum } from 'common/enums';
import { debounce } from 'lodash';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import { getAllowedUnits } from 'utils/forms/cuffa_functions';
import SearchableList from 'components/shared/SearchableList';
import { validateProducaoAgroecologica } from './validation';
import { useProducaoAgroecologicaData } from './useProducaoAgroecologicaData';
import {
  certificationMap,
  reverseCertificationMap as _reverseCertificationMap,
  parseAvailabilityMonths,
  mapApiProductToProdutoSelecionado,
  mapProductListItemToProdutoSelecionado
} from './utils';
import ProdutoItem from './ProdutoItem';
import { updateProdutosField, buildProducaoApiData } from './helpers';
import { ConventionalProduct, Product, ProdutoSelecionado } from '../../types';

interface OtherCertification {
  description: string;
}

export default function ProducaoAgroecologica() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { salvarDadosParciais, dadosParciais } = useCuffaStore();
  const [, setPracticesConventional] = useState<boolean>(false);

  // Estados para produção agroecológica
  const [praticaAgroecologica, setPraticaAgroecologica] = useState<boolean | null>(null);
  const [certificacoes, setCertificacoes] = useState<string[]>([]);
  const [otherCertifications, setOtherCertifications] = useState<OtherCertification[]>([]);
  const [novoOutroCertificacao, setNovoOutroCertificacao] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [produtos, setProdutos] = useState<ProdutoSelecionado[]>([]);
  const [, setLoadingProducts] = useState(true);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Guarda qual campo está em foco e a posição do caret desejada
  const focusedFieldRef = useRef<{ uuid: string; field: string; selectionStart?: number } | null>(null);

  // encapsula os efeitos de carregamento de produtos e dados iniciais
  useProducaoAgroecologicaData(uuid, dadosParciais, {
    setProdutos,
    setPraticaAgroecologica,
    setCertificacoes,
    setOtherCertifications,
    setInitialLoading,
    setSnackbar,
    setLoadingProducts,
    setPracticesConventional
  });

  // certificationMap imported from utils

  // Debounced "persist" (no-op por enquanto) — mantido apenas para cleanup/possível uso futuro
  const debouncedPersist = useMemo(() => debounce((_uuid: string, _campo: string, _valor: any) => {
    // opcional: persistência em background
  }, 500), []);

  // Atualiza o estado imediatamente (mantém o input responsivo)
  const handleProdutoChange = useCallback((uuidParam: string, campo: string, valor: any) => {
    setProdutos(prev => updateProdutosField(prev as any, uuidParam, campo, valor) as any);

    // opcional: dispara persistência debounced sem afetar UI
    debouncedPersist(uuidParam, campo, valor);
  }, [debouncedPersist]);

  useEffect(() => {
    return () => {
      debouncedPersist.cancel();
    };
  }, [debouncedPersist]);

  const carregarDadosDaAPI = useCallback(async () => {
    if (!uuid) return;

    try {
      const response = await CuffaAPI.getDataByID(uuid);

      if (response && response.dados) {
        const apiData = response.dados;

        // Definir se pratica produção convencional
        setPracticesConventional(apiData.family_practices_conventional_production ?? false);

        // Carregar produtos da API
        if (apiData.family_practices_conventional_production && apiData.products) {
          const produtosDaAPI = apiData.products
            .filter((p: any) => p.management_type === 'CONVENCIONAL')
            .map((p: any) => mapApiProductToProdutoSelecionado(p));

          setProdutos(prev => {
            // Manter produtos não selecionados da lista base
            const produtosBase = prev.filter(p => !p.selecionado);
            return [...produtosBase, ...produtosDaAPI];
          });
        }
      }
    } catch (error) {
      console.error("Erro ao carregar dados da API:", error);
      setSnackbar({ open: true, message: 'Erro ao carregar dados salvos.', severity: 'error' });
    }
  }, [uuid]);

  // Buscar produtos disponíveis e carregar dados
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await CuffaAPI.getProducts(['VEGETAL', 'ANIMAL']);
        const produtosFormatados: ProdutoSelecionado[] = response.dados.map((produto: Product) => mapProductListItemToProdutoSelecionado(produto));

        setProdutos(produtosFormatados);

        // Verificar se há dados salvos localmente primeiro
        if (dadosParciais?.producaoAgroecologica && produtosFormatados.length > 0) {
          setPracticesConventional(dadosParciais.producaoAgroecologica.practices_conventional_production);

          setProdutos(prev => prev.map(produto => {
            const savedProduct = dadosParciais.producaoAgroecologica?.conventional_products?.find(
              (sp: any) => sp.product_id === produto.uuid
            );
            if (!savedProduct) return produto;
            return {
              ...produto,
              name: savedProduct.name || produto.name, // Usa o nome salvo se existir
              selecionado: true,
              expandido: false,
              production_area_hectares: savedProduct?.production_area_hectares || null,
              volume_produced: savedProduct?.volume_produced || null,
              volume_produced_unit: (savedProduct?.volume_produced_unit as CuffaMeasurementUnitEnum),
              volume_commercialized: savedProduct?.volume_commercialized || null,
              volume_commercialized_unit: (savedProduct?.volume_commercialized_unit as CuffaMeasurementUnitEnum),
              average_commercialized_value: savedProduct?.average_commercialized_value || null,
              volume_self_consumption: savedProduct?.volume_self_consumption || null,
              volume_self_consumption_unit: (savedProduct?.volume_self_consumption_unit as CuffaMeasurementUnitEnum),
              volume_lost: savedProduct?.volume_lost || null,
              volume_lost_unit: (savedProduct?.volume_lost_unit as CuffaMeasurementUnitEnum),
              is_processed: savedProduct.is_processed,
              production_type: savedProduct.production_type as CuffaProductionTypeEnum,
              product_type: savedProduct.product_type as CuffaProductTypeEnum,
              availability_months: savedProduct?.availability_months || {
                available_year_round: false,
                january: false,
                february: false,
                march: false,
                april: false,
                may: false,
                june: false,
                july: false,
                august: false,
                september: false,
                october: false,
                november: false,
                december: false
              }
            };
          }));
        } else {
          // Se não há dados locais, carregar da API
          await carregarDadosDaAPI();
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar produtos disponíveis.',
          severity: 'error'
        });
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();

    // Cleanup no-op de debounce já tratado em useEffect acima
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosParciais, carregarDadosDaAPI]);

  const reverseCertificationMap = _reverseCertificationMap;

  const handleSelecionarProduto = useCallback((uuidParam: string) => {
    setProdutos(prev => prev.map(p =>
      p.uuid === uuidParam
        ? { ...p, selecionado: !p.selecionado, expandido: !p.selecionado }
        : p
    ));
  }, []);

  const handleExpandirProduto = useCallback((uuidParam: string) => {
    setProdutos(prev => prev.map(p =>
      p.uuid === uuidParam ? { ...p, expandido: !p.expandido } : p
    ));
  }, []);

  // Restaura foco (se houver) após mudança de produtos para evitar perda de foco/scroll jump
  useEffect(() => {
    const focused = focusedFieldRef.current;
    if (!focused) return;

    const el = document.getElementById(`prod-${focused.uuid}-${focused.field}`);
    if (!el) return;

    // Determina um "valor string" seguro do elemento para calcular posição quando necessário.
    let elementValue = '';
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      elementValue = el.value ?? '';
    } else {
      // para selects/custom MUI elements, pega textContent como fallback
      elementValue = (el.textContent ?? '') as string;
    }

    const pos = typeof focused.selectionStart === 'number' ? focused.selectionStart : elementValue.length;

    // Evita qualquer foco/scroll quando o campo for de availability_months...
    const isAvailabilityField = focused.field.startsWith('availability_months');
    if (isAvailabilityField) {
      // não foca nem altera scroll para campos de disponibilidade — isso evita jumps
      return;
    }

    // Só tenta setSelectionRange se o elemento for um input/textarea que suporte seleção.
    const focusWithPreventScroll = (target: HTMLElement) => {
      try {
        (target as any).focus?.({ preventScroll: true });
      } catch (err) {
        try {
          (target as any).focus?.();
        } catch (e) {
          // ignore
        }
      }
    };

    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      try {
        const safePos = Math.max(0, Math.min(pos, (el.value ?? '').length));
        focusWithPreventScroll(el as HTMLElement);
        el.setSelectionRange(safePos, safePos);
      } catch (e) {
        focusWithPreventScroll(el as HTMLElement);
      }
    } else {
      try {
        focusWithPreventScroll(el as HTMLElement);
      } catch (e) {
        // ignore
      }
    }

    try {
      (el as HTMLElement).scrollIntoView({ block: 'nearest', behavior: 'auto' });
    } catch (e) {
      // ignore
    }
  }, [produtos]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID da coleta não encontrado.', severity: 'error' });
        setInitialLoading(false);
        return;
      }

      if (dadosParciais?.producaoAgroecologica) {
        const local = dadosParciais.producaoAgroecologica as any;

        const praticaValue =
          typeof local?.pratica === 'boolean'
            ? local.pratica
            : typeof local?.practices_agroecological_organic === 'boolean'
              ? local.practices_agroecological_organic
              : null;
        setPraticaAgroecologica(praticaValue);

        if (Array.isArray(local?.certificacoes)) {
          setCertificacoes(local.certificacoes);
        } else if (Array.isArray(local?.certification_types)) {
          const mappedCerts = local.certification_types
            .map((cert: string) => {
              const mappedKey = reverseCertificationMap[cert];
              if (mappedKey) return mappedKey;
              if (cert === 'OUTRO') return 'OUTRO';
              if (typeof cert === 'string') {
                if (cert.includes('AUDITORIA')) return 'AUDITORIA';
                if (cert.includes('PARTICIPATIVO') || cert.includes('SPG')) return 'SISTEMA_PARTICIPATIVO';
                if (cert.includes('CONTROLE SOCIAL') || cert.includes('OCS')) return 'CONTROLE_SOCIAL';
              }
              return null;
            })
            .filter((c: string | null): c is string => c !== null);
          setCertificacoes(mappedCerts);
        } else {
          setCertificacoes([]);
        }

        setOtherCertifications(Array.isArray(local?.otherCertifications)
          ? local.otherCertifications
          : Array.isArray(local?.other_certifications)
            ? local.other_certifications
            : []);

        setInitialLoading(false);
        return;
      }
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        if (response && response.dados) {
          const apiData = response.dados;

          const praticaValue = apiData.practices_agroecological_organic ??
            apiData.family_practices_agroecological_organic ??
            null;
          setPraticaAgroecologica(praticaValue);

          if (praticaValue) {
            let tiposCertificacao: string[] = [];
            let outrasCertificacoes: OtherCertification[] = [];

            if (apiData.certification_types) {
              tiposCertificacao = apiData.certification_types;
            } else if (apiData.family_certification_types?.types) {
              tiposCertificacao = apiData.family_certification_types.types;
            }

            if (apiData.other_certifications) {
              outrasCertificacoes = apiData.other_certifications;
            } else if (apiData.family_certification_types?.other_certifications) {
              outrasCertificacoes = apiData.family_certification_types.other_certifications;
            }

            const mappedCerts = tiposCertificacao
              .map((cert: string) => {
                const mappedKey = reverseCertificationMap[cert];
                if (mappedKey) return mappedKey;
                if (cert === 'OUTRO') return 'OUTRO';
                if (cert.includes('AUDITORIA')) return 'AUDITORIA';
                if (cert.includes('PARTICIPATIVO') || cert.includes('SPG')) return 'SISTEMA_PARTICIPATIVO';
                if (cert.includes('CONTROLE SOCIAL') || cert.includes('OCS')) return 'CONTROLE_SOCIAL';
                return null;
              })
              .filter((cert: string | null): cert is string => cert !== null);

            setCertificacoes(mappedCerts);
            setOtherCertifications(outrasCertificacoes);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados de produção agroecológica:', error);
        setSnackbar({ open: true, message: 'Não foi possível carregar os dados.', severity: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const handleCertificacaoChange = (tipo: string) => {
    const isCurrentlyChecked = certificacoes.includes(tipo);

    if (tipo === 'OUTRO' && isCurrentlyChecked) {
      setOtherCertifications([]);
      setNovoOutroCertificacao('');
    }

    setCertificacoes(prev =>
      isCurrentlyChecked ? prev.filter(c => c !== tipo) : [...prev, tipo]
    );
  };

  const handleAdicionarOutroCertificacao = () => {
    const descricao = novoOutroCertificacao.trim();
    if (!descricao) {
      setSnackbar({ open: true, message: 'Digite uma certificação válida.', severity: 'error' });
      return;
    }
    setOtherCertifications(prev => [...prev, { description: descricao }]);
    setNovoOutroCertificacao('');
  };

  const handleRemoverCertificacao = (index: number) => {
    const newOtherCerts = otherCertifications.filter((_, i) => i !== index);
    setOtherCertifications(newOtherCerts);
    if (newOtherCerts.length === 0) {
      setCertificacoes(prev => prev.filter(c => c !== 'OUTRO'));
    }
  };

  const validate = () => {
    const error = validateProducaoAgroecologica(praticaAgroecologica, certificacoes, otherCertifications);
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return false;
    }
    return true;
  };

  const handleSave = async (navigateNext = false) => {
    if (!validate() || !uuid) return;

    try {
      setSaving(true);

      const apiData = buildProducaoApiData(praticaAgroecologica, certificacoes, otherCertifications, produtos, certificationMap);

      try {
        await CuffaAPI.updateFamilyCertification(uuid, apiData);
      } catch (error) {
        console.warn('Método updateFamilyCertification falhou, tentando updateData...', error);
      }

      // Salvar no storage local incluindo o nome do produto em cada produto
      const dataToSave = {
        pratica: praticaAgroecologica,
        certificacoes,
        otherCertifications,
        conventional_products: produtos
          .filter(p => p.selecionado)
          .map(p => ({
            product_id: p.uuid,
            name: p.name, // Salva o nome do produto também
            production_area_hectares: p.production_area_hectares,
            volume_produced: p.volume_produced,
            volume_produced_unit: p.volume_produced_unit,
            volume_commercialized: p.volume_commercialized,
            volume_commercialized_unit: p.volume_commercialized_unit,
            average_commercialized_value: p.average_commercialized_value,
            volume_self_consumption: p.volume_self_consumption,
            volume_self_consumption_unit: p.volume_self_consumption_unit,
            volume_lost: p.volume_lost,
            volume_lost_unit: p.volume_lost_unit,
            availability_months: p.availability_months
          }))
      };

      salvarDadosParciais({
        producaoAgroecologica: dataToSave,
        ultimaRota: location.pathname,
      });

      await CuffaAPI.updateConventionalProduction(uuid, apiData);
      salvarDadosParciais({
        producaoAgroecologica: apiData,
        ultimaRota: location.pathname,
      });

      setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });

      if (navigateNext) {
        setTimeout(() => navigate(ROUTES.DETALHES_PRODUCAO_INCLUIR.replace(':uuid', uuid)), 1000);
      }
    } catch (error) {
      console.error('Erro ao salvar certificação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar os dados. Verifique os valores e tente novamente.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // ProdutoDetalhes and ProdutoItem extracted to separate components

  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produção Agroecológica ou Orgânica" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  const radioValue = praticaAgroecologica === null ? '' : praticaAgroecologica ? 'sim' : 'nao';

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Produção Agroecológica ou Orgânica" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Prática de Produção Agroecológica ou Orgânica" width="100%">
            <Box mb={4}>
              <Typography fontWeight="bold" gutterBottom>
                A unidade familiar pratica produção agroecológica ou orgânica? *
              </Typography>
              <RadioGroup
                row
                value={radioValue}
                onChange={(e) => {
                  const value = e.target.value === 'sim';
                  setPraticaAgroecologica(value);
                  if (!value) {
                    setCertificacoes([]);
                    setOtherCertifications([]);
                  }
                }}
              >
                <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                <FormControlLabel value="nao" control={<Radio />} label="Não" />
              </RadioGroup>
            </Box>

            {praticaAgroecologica === true && (
              <>
                <Typography fontWeight="bold" gutterBottom>
                  Tipo de entidade certificadora da produção *
                </Typography>
                <Box display="flex" flexDirection="column" gap={1} mb={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={certificacoes.includes('AUDITORIA')}
                        onChange={() => handleCertificacaoChange('AUDITORIA')}
                        name="AUDITORIA"
                      />
                    }
                    label="Certificação por auditoria (OCS)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={certificacoes.includes('SISTEMA_PARTICIPATIVO')}
                        onChange={() => handleCertificacaoChange('SISTEMA_PARTICIPATIVO')}
                        name="SISTEMA_PARTICIPATIVO"
                      />
                    }
                    label="Sistema Participativo de Garantia (SPG)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={certificacoes.includes('CONTROLE_SOCIAL')}
                        onChange={() => handleCertificacaoChange('CONTROLE_SOCIAL')}
                        name="CONTROLE_SOCIAL"
                      />
                    }
                    label="Controle Social na venda direta"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={certificacoes.includes('OUTRO')}
                        onChange={() => handleCertificacaoChange('OUTRO')}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        Outro:
                        <TextField
                          size="small"
                          value={novoOutroCertificacao}
                          onChange={(e) => setNovoOutroCertificacao(e.target.value.toLocaleUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAdicionarOutroCertificacao();
                            }
                          }}
                          disabled={!certificacoes.includes('OUTRO')}
                        />
                        <IconButton
                          color="primary"
                          onClick={handleAdicionarOutroCertificacao}
                          disabled={!novoOutroCertificacao.trim() || !certificacoes.includes('OUTRO')}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    }
                  />
                </Box>
                {otherCertifications.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Outras certificações adicionadas:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {otherCertifications.map((cert, index) => (
                        <Chip
                          key={index}
                          label={cert.description}
                          onDelete={() => handleRemoverCertificacao(index)}
                          deleteIcon={<DeleteIcon />}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <>
                  <Typography variant="h6" gutterBottom>
                    Produtos Convencionais Produzidos
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Selecione os itens produzidos no modo convencional:
                  </Typography>

                  <SearchableList
                    items={produtos}
                    getLabel={(p) => p.name}
                    placeholder="Buscar produto..."
                    listRef={scrollContainerRef}
                    listSx={{
                      maxHeight: '500px',
                      mb: 3,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      overflow: 'auto',
                      p: 1
                    }}
                  >
                    {(produto: ProdutoSelecionado) => (
                      <ProdutoItem
                        key={produto.uuid}
                        produto={produto}
                        onSelecionar={handleSelecionarProduto}
                        onExpandir={handleExpandirProduto}
                        onChange={handleProdutoChange}
                        focusedFieldRef={focusedFieldRef}
                      />
                    )}
                  </SearchableList>
                </>

              </>
            )}

            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.CATEGORIA_FUNDIARIA_INCLUIR.replace(':uuid', uuid ?? ''))}>
                Retornar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSave(true)}
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Avançar'}
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
}
